import React, { createRef, RefObject, useMemo, useState } from 'react';

import { Form } from '@duxton-web/ui';
import {
  generatePersonalAttributesHTML,
  getCaretPositionAndUpdatedContentForCustomInput,
} from 'omega-rtc/utils/smart-campaign-utils';

import './SmartCampaignContentVariants.scss';

// Types
interface SmartCampaignCustomInputProps {
  path: string[];
  minRows?: number;
  placeholder?: string;
  errorMessage?: string;
}

type InputHistoryType = {
  value: string;
  caretPosition: number;
};
type InputHistoryListType = InputHistoryType[];

const TOOL_TIP_CLASS_NAME = 'tool-tip-text';

// Utils
const restoreSelection = (
  element: HTMLElement,
  previousCaretPosition: number,
) => {
  const selection = window.getSelection();
  if (!previousCaretPosition || !selection) {
    return;
  }

  const range = document.createRange();
  let charCount = 0;
  let moveToNext = false;

  const traverseNodes = (node: Node) => {
    // Stop traversing if the character count exceeds the end of the selection range.
    if (charCount >= previousCaretPosition && !moveToNext) {
      return;
    }

    // If the node is not a text node, traverse its child nodes.
    if (node.nodeType !== Node.TEXT_NODE) {
      // @ts-ignore
      if (node.className === TOOL_TIP_CLASS_NAME) {
        // we need to ignore all tooltips
        return;
      }

      for (let i = 0; i < node.childNodes.length; i++) {
        traverseNodes(node.childNodes[i]);
      }
      return;
    }
    const textContent = node.textContent || '';
    const textLength = textContent.length;
    moveToNext =
      charCount + textLength === previousCaretPosition &&
      ['{', '}'].includes(textContent);

    // If the start position is within the current node, set the start of the range.
    if (charCount + textLength >= previousCaretPosition) {
      range.setStart(node, previousCaretPosition - charCount);
    }
    charCount += textLength;
  };

  traverseNodes(element);

  // Clear any existing selection ranges and add the new range.
  selection.removeAllRanges();
  selection.addRange(range);

  adjustSelectionFromNonEditableContainer(element);
};

// Function to adjust the selection if it is within a non-editable container
const adjustSelectionFromNonEditableContainer = (
  parentElement: HTMLElement,
) => {
  const range = document.createRange();
  const currentSelection = window.getSelection();
  const focusNode = currentSelection && currentSelection.focusNode;
  const parentElementOfFocusNode = focusNode && focusNode.parentElement;

  // Check if the parent element of the focus node is non-editable
  if (
    parentElementOfFocusNode &&
    parentElementOfFocusNode.contentEditable === 'false'
  ) {
    // Get the next sibling of the non-editable container
    const nextSiblingElement = parentElementOfFocusNode.nextSibling;

    // If next sibling is undefined, move the selection to the specified element
    range.setStart(nextSiblingElement || parentElement, 0);
    currentSelection.removeAllRanges();
    currentSelection.addRange(range);
  }
};

const isSelectionWithinClassContainer = (className: string) => {
  const selection = window.getSelection();
  const focusNode = selection.focusNode || {};
  return [
    // @ts-ignore
    (focusNode.parentElement || {}).className || '',
    // @ts-ignore
    focusNode.className || '',
  ].includes(className);
};

const hideAllTooltips = ({ ref, toolTips: existingTooltips = '' }) => {
  if (!ref || !ref.current) {
    return;
  }

  const toolTips: any =
    existingTooltips || ref.current.getElementsByClassName(TOOL_TIP_CLASS_NAME);
  for (let i = 0; i < toolTips.length; i++) {
    toolTips[i].hidden = true;
  }
};

// Main Component
const SmartCampaignCustomInput = ({
  path,
  placeholder,
  errorMessage,
  minRows = 1,
}: SmartCampaignCustomInputProps) => {
  const form = Form.useFormInstance();
  const ref: RefObject<HTMLParagraphElement> = createRef();
  const [inputHistoryPointer, setInputHistoryPointer] = useState(0);
  const [isConversionTriggered, setIsConversionTriggered] = useState(false);
  const [isInDeleteMode, setIsInDeleteMode] = useState(false);

  const initialFormValue = useMemo(() => {
    const formValue = form.getFieldValue(path) || '';
    return {
      value: formValue,
      caretPosition: formValue.length,
    };
  }, []);
  const [inputHistory, setInputHistory] = useState<InputHistoryListType>([
    initialFormValue,
  ]);

  const initialValue = useMemo(() => {
    const htmlContent = generatePersonalAttributesHTML(
      form.getFieldValue(path),
    ).trimEnd();
    // If we don't have htmlContent then, we don't need to append space. If yes, If we not add a space, then, default pointer will go into our label span.
    return htmlContent ? htmlContent + ' ' : '';
  }, []);

  const showErrorMessage = useMemo(() => {
    return form.getFieldValue(path) === '' ? errorMessage : '';
  }, [inputHistoryPointer, errorMessage]);

  const handleUndo = () => {
    if (inputHistoryPointer - 1 < 0) {
      return;
    }
    const valueInHistory = inputHistory[inputHistoryPointer - 1];
    updateText(valueInHistory.value, true, valueInHistory.caretPosition);
    setInputHistoryPointer(inputHistoryPointer - 1);
  };

  const handleRedo = () => {
    if (inputHistoryPointer + 1 > inputHistory.length - 1) {
      return;
    }
    const valueInHistory = inputHistory[inputHistoryPointer - 1];
    updateText(valueInHistory.value, true, valueInHistory.caretPosition);
    setInputHistoryPointer(inputHistoryPointer + 1);
  };

  const updateText = (
    newValue: string,
    isUndoEvent = false,
    caretPositionOnUndoOrRedo = undefined, // only pass this value if in undo or redo mode
  ) => {
    if (!ref || !ref.current) {
      return;
    }
    const { updatedContent, startOfCaret, updateInnerHTML } =
      getCaretPositionAndUpdatedContentForCustomInput({
        element: ref.current,
        isInDeleteMode,
        latestValue: newValue,
        previousValue: form.getFieldValue(path),
      });

    if (!isUndoEvent) {
      setInputHistory((prev) => {
        const newList =
          inputHistoryPointer < prev.length - 1
            ? prev.slice(0, inputHistoryPointer)
            : prev;

        return [
          ...newList,
          { value: updatedContent, caretPosition: startOfCaret },
        ];
      });
      setInputHistoryPointer((prev) => prev + 1);
    }
    form.setFieldValue(path, updatedContent);

    if (isConversionTriggered || updateInnerHTML || isUndoEvent) {
      const caretPosition = caretPositionOnUndoOrRedo || startOfCaret;
      const formValue = form.getFieldValue(path);
      ref.current.innerHTML =
        generatePersonalAttributesHTML(formValue).trimEnd() + ' ';
      restoreSelection(
        ref.current,
        caretPosition < 0 || caretPosition > formValue.length
          ? formValue.length
          : caretPosition,
      );
      setIsConversionTriggered(false);
    }
  };

  const handleInput = (event: React.FormEvent<HTMLParagraphElement>) => {
    const toolTips: any =
      ref.current.getElementsByClassName(TOOL_TIP_CLASS_NAME);
    // Hiding all toolTip, because if tootTip is currently active then, it will get appended into `event.currentTarget.innerText` we need to remove it.
    hideAllTooltips({ ref, toolTips });
    const inputText = event.currentTarget.innerText;
    const newText = isInDeleteMode && inputText === '\n' ? '' : inputText;
    updateText(newText);
    setIsInDeleteMode(false);

    for (let i = 0; i < toolTips.length; i++) {
      toolTips[i].hidden = false;
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLParagraphElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      hideAllTooltips({ ref });
      setIsInDeleteMode(true);
      return;
    }

    // To prevent user from pressing enter key from label container
    if (
      event.key === 'Enter' &&
      isSelectionWithinClassContainer('editable-input-label-container')
    ) {
      event.preventDefault();
      return;
    }

    const isMacOs = window.navigator.userAgent.indexOf('Mac') !== -1;
    const isCtrlKeyActive = isMacOs ? event.metaKey : event.ctrlKey; // Determine if the control key is active (Cmd on Mac, Ctrl on other OS). event.metaKey will true in Mac.

    if (isCtrlKeyActive && (event.key === 'z' || event.key === 'Z')) {
      event.preventDefault();
      if (event.shiftKey) {
        handleRedo(); // If Shift key is also pressed, perform redo operation
        return;
      }

      handleUndo(); // Otherwise, perform undo operation
      return;
    }
  };

  return (
    <>
      <p
        ref={ref}
        style={{
          minHeight: 28 * minRows,
          lineHeight: minRows > 2 ? '24px' : '17px',
        }}
        className={`${showErrorMessage ? 'editable-text-input-error editable-text-input' : 'editable-text-input'}`}
        contentEditable={'plaintext-only'}
        onInput={handleInput}
        onPaste={() => {
          setIsConversionTriggered(true);
        }}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        dangerouslySetInnerHTML={{
          __html: initialValue,
        }}
      />
      {showErrorMessage && (
        <span className="editable-text-input-explain-error">
          {showErrorMessage}
        </span>
      )}
    </>
  );
};

export default SmartCampaignCustomInput;
